import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import BeforeAfterSlider from "@/components/site/BeforeAfterSlider";
import AnimatedCounter from "@/components/site/AnimatedCounter";
import CounsellorCallbackCard from "@/components/site/CounsellorCallbackCard";
import { useCms } from "@/lib/useCms";
import { api } from "@/lib/api";
import {
    Scan, Package, SmileyWink, ShieldCheck, Clock, CurrencyInr,
    CheckCircle, Star, ArrowRight, Sparkle, MapPin
} from "@phosphor-icons/react";

const STEPS = [
    {
        icon: MapPin,
        title: "Find Your Doctor",
        desc: (
            <>
                Skip the search stress. The Invisique™ portal surfaces only the <span className="text-foreground font-semibold">best, top-rated doctors near you</span>. Read reviews, explore profiles, and book your consultation with the provider you choose. One click to browse, one click to book. Simple.
            </>
        ),
    },
    {
        icon: Scan,
        title: "Scan, Plan & Preview",
        desc: (
            <>
                Your Invisique™ Provider deploys <span className="text-foreground font-semibold">cutting-edge intraoral scanning</span> to map every curve and contour of your teeth in stunning digital detail. Walk away with a live preview of your future smile and a custom treatment plan built entirely around your teeth, your timeline, and your goals.
            </>
        ),
    },
    {
        icon: Package,
        title: "Invisique™ Aligners, Made for You",
        desc: (
            <>
                Crafted through <span className="text-foreground font-semibold">German-engineered digital workflows</span> and precision biomechanics, your Invisique™ aligners arrive crystal-clear and ready to work from day one. Each tray is clinically optimised for your exact smile stage, engineered around your anatomy, your bite, and your life.
            </>
        ),
    },
];

const TRANSFORMATIONS = [
    {
        id: "t1",
        name: "Ananya, 16",
        case: "Crowding + alignment",
        complexity: "Moderate",
        aligners: 26,
        months: 8,
        city: "Bengaluru",
        quote: "I used to cover my mouth when I laughed. Eight months later, I can't stop smiling. My orthodontist explained every step and the aligners were so comfortable I forgot I was wearing them.",
        composite: "/marketing/patient1.png",
    },
    {
        id: "t2",
        name: "Priya, 26",
        case: "Anterior spacing",
        complexity: "Simple",
        aligners: 18,
        months: 10,
        city: "Chennai",
        quote: "The gap in my front teeth made me self-conscious for years. With Invisique™, the gap closed gently and naturally, no one at work even noticed I was in treatment.",
        composite: "/marketing/patient2.png",
    },
    {
        id: "t3",
        name: "Arjun, 15",
        case: "Crowding",
        complexity: "Moderate",
        aligners: 28,
        months: 9,
        city: "Mumbai",
        quote: "My friends at school didn't even realise I had aligners. I played cricket every weekend and never had to skip a match. The app reminders kept me on track.",
        composite: "/marketing/patient3.png",
    },
];

/* Real stories cards for the homepage "Results" / "Real stories" section.
   iter100.150 — Fetched at runtime from `/api/testimonials?scope=home`
   (Testimonials Manager). This array is now just the code-side
   fallback used if the API is unreachable / empty. */
const REAL_STORIES_FALLBACK = [
    {
        id: "rs1",
        name: "Vikram & son Aarav",
        case: "Crowding · 9 months",
        city: "Bengaluru",
        quote: "He finished his treatment last month. Watching Aarav laugh on stage at his school function without covering his mouth — that's what I paid for.",
        image: "/marketing/P1.png",
    },
    {
        id: "rs2",
        name: "Meera, 32",
        case: "Anterior spacing · 10 months",
        city: "Pune",
        quote: "Three colleagues asked which orthodontist I was seeing — none of them realised I was already six months into treatment. That's how invisible it felt at work.",
        image: "/marketing/P2.png",
    },
    {
        id: "rs3",
        name: "Priya, 17",
        case: "Mild crowding · 7 months",
        city: "Hyderabad",
        quote: "My friends thought I'd just gotten my teeth whitened. The aligners were so comfortable I forgot I had them in during school.",
        image: "/marketing/P3.png",
    },
    {
        id: "rs4",
        name: "Anjali, 24",
        case: "Crossbite · 11 months",
        city: "Delhi NCR",
        quote: "Mine was complex — I'd been told braces were the only option. Invisique™'s COE built me a plan that worked, and I never once felt like a number.",
        image: "/marketing/P4.png",
    },
];

const COMPLEXITY_TONE = {
    Simple: "bg-emerald-100 text-emerald-700 border-emerald-200",
    Moderate: "bg-amber-100 text-amber-800 border-amber-200",
    Complex: "bg-rose-100 text-rose-700 border-rose-200",
};

const BENEFITS = [
    {
        icon: ShieldCheck,
        title: "Nearly Invisible",
        desc: (
            <>
                <span className="text-foreground font-semibold">Clear, smooth aligners</span> so comfortable and discreet, no one will notice, except you.
            </>
        ),
    },
    {
        icon: Clock,
        title: "Fewer Clinic Visits",
        desc: (
            <>
                <span className="text-foreground font-semibold">Check in every 2 to 3 weeks</span> and let your aligners do the work. Your life doesn't pause for your smile.
            </>
        ),
    },
    {
        icon: CurrencyInr,
        title: "Cost-Effective Treatment",
        desc: (
            <>
                <span className="text-foreground font-semibold">Built to Be Affordable</span>, no hidden costs, no surprises. World-class aligners at a price that makes sense.
            </>
        ),
    },
    {
        icon: CheckCircle,
        title: "Certified Providers",
        desc: (
            <>
                <span className="text-foreground font-semibold">Only board-certified doctors.</span> Every provider, every city, every smile, held to the highest clinical standard.
            </>
        ),
    },
];

const FAQ = [
    {
        q: "What are Invisique™ Clear Aligners, and how are they different from traditional braces?",
        a: (
            <>
                <p>
                    Invisique™ Clear Aligners are a series of custom-made, transparent, removable orthodontic trays that gently shift your teeth into their ideal position, without metal wires, brackets, or the discomfort that comes with them.
                </p>
                <p className="mt-3">
                    Unlike traditional braces that are fixed to your teeth for the entire duration of treatment, Invisique™ Clear Aligners can be removed when you eat, drink, or brush your teeth. They are virtually invisible when worn, making them the preferred choice for professionals, students, and anyone who values aesthetics during treatment.
                </p>

                {/* Comparison table */}
                <div className="mt-5 overflow-x-auto rounded-xl border border-border">
                    <table className="w-full text-sm">
                        <thead className="bg-secondary/60">
                            <tr>
                                <th className="text-left font-semibold text-foreground px-4 py-3">Feature</th>
                                <th className="text-left font-semibold text-primary px-4 py-3">Invisique™ Clear Aligners</th>
                                <th className="text-left font-semibold text-foreground/70 px-4 py-3">Traditional Braces</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {[
                                ["Visibility", "Nearly invisible", "Clearly visible metal"],
                                ["Comfort", "Smooth plastic, no sharp edges", "Wires & brackets can irritate"],
                                ["Removability", "Yes, for eating & hygiene", "No, fixed throughout"],
                                ["Diet Restrictions", "None", "Several (no hard/sticky foods)"],
                                ["Clinic Visits", "Fewer, less frequent", "More frequent"],
                                ["Oral Hygiene", "Easy. Brush & floss normally", "Challenging around brackets"],
                            ].map((row) => (
                                <tr key={row[0]} className="bg-white">
                                    <td className="px-4 py-3 font-semibold text-foreground">{row[0]}</td>
                                    <td className="px-4 py-3 text-foreground/85">{row[1]}</td>
                                    <td className="px-4 py-3 text-muted-foreground">{row[2]}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </>
        ),
    },
    {
        q: "Am I a suitable candidate for Invisique™ Clear Aligner treatment?",
        a: (
            <>
                <p>
                    Invisique™ Clear Aligners are an excellent solution for a wide range of orthodontic conditions. You may be a good candidate if you have any of the following:
                </p>
                <ul className="mt-3 space-y-1.5 list-disc pl-5">
                    <li><span className="font-semibold text-foreground">Crowded teeth</span>: teeth overlapping due to insufficient space</li>
                    <li><span className="font-semibold text-foreground">Spacing issues</span>: gaps or spaces between teeth</li>
                    <li><span className="font-semibold text-foreground">Overbite</span>: upper teeth overlapping excessively over the lower</li>
                    <li><span className="font-semibold text-foreground">Underbite</span>: lower teeth sitting in front of the upper teeth</li>
                    <li><span className="font-semibold text-foreground">Crossbite</span>: upper and lower teeth misaligned side to side</li>
                    <li><span className="font-semibold text-foreground">Open bite</span>: front teeth that don't meet when the mouth is closed</li>
                </ul>
                <p className="mt-3">
                    Invisique™ Clear Aligners can treat mild to moderately complex cases across all age groups, from teenagers and working professionals to adults. The best way to know for certain is to visit your doctor, who will conduct a thorough clinical evaluation and a 3D digital scan to assess your suitability.
                </p>
                <div className="mt-4 flex items-start gap-2 p-3 rounded-xl bg-primary/5 border border-primary/15">
                    <Sparkle size={14} weight="fill" className="text-primary mt-0.5 shrink-0" />
                    <p className="text-xs text-foreground/80 italic">
                        A personalised assessment by a qualified doctor is always the first step.
                    </p>
                </div>
            </>
        ),
    },
    {
        q: "Are Invisique™ Clear Aligners painful?",
        a: (
            <p>
                Invisique™ Clear Aligners are significantly more comfortable than traditional braces. However, it is perfectly normal to experience mild pressure or slight tightness for the first 2 to 3 days after switching to a new set of aligners.
            </p>
        ),
    },
    {
        q: "What is the cost of Invisique™ Clear Aligner treatment?",
        a: (
            <>
                <p>
                    The cost of Invisique™ Clear Aligner treatment varies from patient to patient, as it is determined entirely by your treating doctor based on the unique requirements of your case. Pricing is not fixed or standardised, and rightly so, because no two smiles are alike.
                </p>
                <p className="mt-3">
                    Your doctor will assess several clinical factors before recommending a treatment plan and associated fees, including:
                </p>
                <ul className="mt-3 space-y-1.5 list-disc pl-5">
                    <li><span className="font-semibold text-foreground">Complexity and severity</span> of your orthodontic condition</li>
                    <li><span className="font-semibold text-foreground">Number of aligners</span> required to achieve the desired outcome</li>
                    <li><span className="font-semibold text-foreground">Duration of treatment</span>: longer cases naturally involve more stages and refinements</li>
                    <li>Inclusion of <span className="font-semibold text-foreground">additional procedures</span> such as attachments, refinements, or post-treatment retainers</li>
                    <li><span className="font-semibold text-foreground">Clinic location and infrastructure</span>: fees may reflect the facilities and expertise available at your chosen clinic</li>
                </ul>
                <p className="mt-4">
                    We strongly encourage you to schedule a consultation with your doctor, who will provide a precise, transparent treatment plan tailored specifically to your case, with complete clarity on what is included.
                </p>
                <div className="mt-4 flex items-start gap-2 p-3 rounded-xl bg-primary/5 border border-primary/15">
                    <Sparkle size={14} weight="fill" className="text-primary mt-0.5 shrink-0" />
                    <p className="text-xs text-foreground/80 italic">
                        When consulting your doctor, ask about what is covered in your treatment package, including refinements, retainers, and follow-up visits, so you have a complete picture before beginning your smile journey.
                    </p>
                </div>
            </>
        ),
    },
    {
        q: "How long does Invisique™ Clear Aligner treatment take?",
        a: (
            <>
                <p>
                    Treatment duration varies depending on the complexity of your case. As a general guide:
                </p>
                <ul className="mt-3 space-y-1.5 list-disc pl-5">
                    <li><span className="font-semibold text-foreground">Mild misalignment</span>: 6 to 9 months</li>
                    <li><span className="font-semibold text-foreground">Moderate cases</span>: 9 to 15 months</li>
                    <li><span className="font-semibold text-foreground">Complex bite corrections</span>: 15 to 24 months or more</li>
                </ul>
                <p className="mt-3">
                    Your doctor will give you a personalised timeline based on your 3D scan and treatment plan. One important factor entirely in your hands: wearing your aligners for the recommended <span className="font-semibold text-foreground">22 hours per day</span> significantly determines whether your treatment stays on schedule.
                </p>
                <div className="mt-4 flex items-start gap-2 p-3 rounded-xl bg-primary/5 border border-primary/15">
                    <Sparkle size={14} weight="fill" className="text-primary mt-0.5 shrink-0" />
                    <p className="text-xs text-foreground/80 italic">
                        Consistency is the most powerful tool in your treatment.
                    </p>
                </div>
            </>
        ),
    },
    {
        q: "How many hours a day do I need to wear my aligners?",
        a: (
            <>
                <p>
                    You must wear your Invisique™ Clear Aligners for a minimum of <span className="font-semibold text-foreground">20 to 22 hours every day</span>, including while you sleep. This is non-negotiable for effective treatment.
                </p>
                <p className="mt-3 font-semibold text-foreground">You should only remove your aligners when:</p>
                <ul className="mt-2 space-y-1.5 list-disc pl-5">
                    <li>Eating or drinking (anything other than plain, cool water)</li>
                    <li>Brushing and flossing your teeth</li>
                </ul>
                <p className="mt-3">
                    Failing to wear your aligners for the prescribed hours can delay your progress and, in some cases, compromise your final results. If you regularly miss hours, your doctor may recommend extending the wear period for that particular tray before moving to the next one.
                </p>
                <div className="mt-4 flex items-start gap-2 p-3 rounded-xl bg-primary/5 border border-primary/15">
                    <Sparkle size={14} weight="fill" className="text-primary mt-0.5 shrink-0" />
                    <p className="text-xs text-foreground/80 italic">
                        Every hour counts. The more disciplined you are, the faster and better your results.
                    </p>
                </div>
            </>
        ),
    },
    {
        q: "Can I eat and drink normally during Invisique™ Clear Aligner treatment?",
        a: (
            <>
                <p>
                    Yes. One of the biggest advantages of Invisique™ Clear Aligners is that there are no dietary restrictions whatsoever.
                </p>
                <p className="mt-3">
                    Since aligners are fully removable, you can enjoy all your favourite foods without any concern about damaging your orthodontic appliance. No avoiding rotis, no skipping on sugarcane, no saying no to your favourite snacks.
                </p>

                <div className="mt-5 grid sm:grid-cols-2 gap-4">
                    <div className="rounded-xl bg-green-50/70 border border-green-200/60 p-4">
                        <div className="font-display font-semibold text-sm text-foreground mb-2">Always Do</div>
                        <ul className="space-y-1.5 text-xs text-foreground/85">
                            <li>✅ Remove aligners before eating or drinking (except cool water)</li>
                            <li>✅ Brush your teeth before reinserting your aligners after meals</li>
                            <li>✅ Rinse your aligners with cool water before putting them back in</li>
                        </ul>
                    </div>
                    <div className="rounded-xl bg-red-50/70 border border-red-200/60 p-4">
                        <div className="font-display font-semibold text-sm text-foreground mb-2">Never Do</div>
                        <ul className="space-y-1.5 text-xs text-foreground/85">
                            <li>❌ Drink hot beverages, tea, coffee, or coloured drinks with aligners in. They can stain and warp the trays</li>
                            <li>❌ Chew gum with aligners on</li>
                            <li>❌ Eat with aligners in. Ever.</li>
                        </ul>
                    </div>
                </div>

                <div className="mt-4 flex items-start gap-2 p-3 rounded-xl bg-primary/5 border border-primary/15">
                    <Sparkle size={14} weight="fill" className="text-primary mt-0.5 shrink-0" />
                    <p className="text-xs text-foreground/80 italic">
                        Remove. Eat freely. Brush. Reinsert. That's the only routine you need to remember.
                    </p>
                </div>
            </>
        ),
    },
    {
        q: "How do I clean and maintain my Invisique™ Clear Aligners?",
        a: (
            <>
                <p>
                    Maintaining your Invisique™ Clear Aligners is simple and takes only a few minutes daily. Follow this routine:
                </p>
                <p className="mt-3 font-semibold text-foreground">Daily Cleaning Routine:</p>
                <ul className="mt-2 space-y-1.5 list-disc pl-5">
                    <li>Rinse aligners with cool water every time you remove them</li>
                    <li>Clean gently with a soft-bristled toothbrush, morning and night, using plain water or a mild, clear soap</li>
                    <li>Never use toothpaste directly on aligners. It is abrasive and can cause micro-scratches that cloud the surface</li>
                    <li>Do not soak in mouthwash, hot water, or bleach. These damage the material</li>
                    <li>Store in the provided hard case whenever not in use. Never wrap in a tissue or leave on an open surface</li>
                </ul>

                <div className="mt-5 rounded-xl bg-red-50/70 border border-red-200/60 p-4">
                    <div className="font-display font-semibold text-sm text-foreground mb-2">What to Avoid</div>
                    <ul className="space-y-1.5 text-xs text-foreground/85">
                        <li>❌ Toothpaste. Too abrasive</li>
                        <li>❌ Hot water. Warps the tray shape</li>
                        <li>❌ Coloured soaps or mouthwash. Causes staining</li>
                        <li>❌ Leaving aligners exposed in open air for long periods</li>
                    </ul>
                </div>

                <p className="mt-4">
                    Equally important: maintain excellent oral hygiene throughout your treatment. Brush and floss thoroughly after every meal before reinserting your aligners.
                </p>
                <div className="mt-4 flex items-start gap-2 p-3 rounded-xl bg-primary/5 border border-primary/15">
                    <Sparkle size={14} weight="fill" className="text-primary mt-0.5 shrink-0" />
                    <p className="text-xs text-foreground/80 italic">
                        Clean aligners equal invisible aligners. A two-minute routine protects your entire investment.
                    </p>
                </div>
            </>
        ),
    },
    {
        q: "What happens after my Invisique™ Clear Aligner treatment is complete?",
        a: (
            <>
                <p>
                    Completing your aligner treatment is a major milestone, but it is not the final step. The phase that follows is called <span className="font-semibold text-foreground">retention</span>, and it is just as important as the treatment itself.
                </p>
                <p className="mt-3">
                    Once your final tray is worn and your teeth have reached their desired positions, your doctor will prescribe a retainer. Retainers hold your teeth in their new positions and prevent them from gradually shifting back, a natural tendency called <span className="font-semibold text-foreground">relapse</span>.
                </p>

                <p className="mt-4 font-semibold text-foreground">Types of Retainers:</p>
                <ul className="mt-2 space-y-1.5 list-disc pl-5">
                    <li><span className="font-semibold text-foreground">Fixed Retainer</span>: a thin wire bonded to the back of your teeth. Invisible and maintenance-free</li>
                    <li><span className="font-semibold text-foreground">Removable Retainer</span>: a clear tray similar to your aligners, typically worn every night indefinitely</li>
                </ul>

                <p className="mt-4 font-semibold text-foreground">What to Expect Post-Treatment:</p>
                <ul className="mt-2 space-y-1.5 list-disc pl-5">
                    <li>Your doctor will schedule a final review appointment</li>
                    <li>Retainer wear instructions will be provided. Follow them strictly</li>
                    <li>Periodic check-ins may be recommended every 6 to 12 months</li>
                    <li>Minor tooth movement is normal in the first few weeks post-treatment. Do not panic; your retainer will stabilise this</li>
                </ul>

                <div className="mt-4 flex items-start gap-2 p-3 rounded-xl bg-primary/5 border border-primary/15">
                    <Sparkle size={14} weight="fill" className="text-primary mt-0.5 shrink-0" />
                    <p className="text-xs text-foreground/80 italic">
                        Treatment ends. Results last forever, but only if you wear your retainer. No exceptions.
                    </p>
                </div>
            </>
        ),
    },
];

export default function LandingPage() {
    const [activeCase, setActiveCase] = React.useState(TRANSFORMATIONS[0]);
    // iter100.123 — hero copy is CMS-driven; fallbacks preserve the
    // pre-CMS strings so page never renders empty mid-load.
    const { t: h } = useCms("hero");

    /* iter100.150 — Real Stories now driven by the Testimonials Manager.
       Falls back to the code-side `REAL_STORIES_FALLBACK` if the API
       is unreachable OR returns empty. */
    const [realStories, setRealStories] = useState(REAL_STORIES_FALLBACK);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/testimonials", { params: { scope: "home", limit: 12 } });
                if (cancelled) return;
                if (Array.isArray(data.items) && data.items.length > 0) {
                    setRealStories(data.items.map((r) => ({
                        id: r.id,
                        name: r.name,
                        case: r.case_label,
                        city: r.city,
                        quote: r.quote,
                        image: r.image_url,
                    })));
                }
            } catch {
                // Keep the hardcoded fallback on error — never render blank.
            }
        })();
        return () => { cancelled = true; };
    }, []);

    return (
        <div className="font-body" data-testid="landing-page">
            {/* HERO */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 blue-grid-bg opacity-60 mask-fade-b pointer-events-none" />
                <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 lg:pt-24 pb-16 lg:pb-28 grid lg:grid-cols-12 gap-10 items-center relative">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="lg:col-span-7"
                    >
                        {/* Trust pill */}
                        <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-white border border-border/70 shadow-sm mb-6" data-testid="hero-trust-pill">
                            <div className="flex items-center gap-0.5 text-amber-500">
                                {["a", "b", "c", "d", "e"].map((k) => (
                                    <Star key={k} size={13} weight="fill" />
                                ))}
                            </div>
                            <span className="text-xs font-semibold text-foreground">
                                {h("trust_pill_rating", "4.9")} <span className="text-muted-foreground font-normal">{h("trust_pill_suffix", "from verified patients")}</span>
                            </span>
                        </div>
                        <div className="overline text-primary mb-5" data-testid="hero-overline">{h("overline", "Indian smiles, world-class aligners")}</div>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight" data-testid="hero-headline">
                            {h("headline_top", "Invisible Precision,")}<br /><span className="text-primary">{h("headline_accent", "Uniquely Yours.")}</span>
                        </h1>
                        <p className="mt-6 text-lg text-muted-foreground max-w-xl" data-testid="hero-subheadline">
                            {h("subheadline", "Invisique™ Clear Aligners are clinically optimized for Indian dento-facial morphology, with precision biomechanics and German-engineered digital workflows enabling faster results & better compliance.")}
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/smile-simulator" className="relative group" data-testid="hero-simulator-link">
                                {/* Animated shimmer halo */}
                                <span
                                    className="absolute -inset-1 rounded-full bg-gradient-to-r from-primary via-cyan-400 to-primary bg-[length:200%_100%] opacity-80 blur-md group-hover:opacity-100 transition-opacity"
                                    style={{ animation: "shimmer 3s linear infinite" }}
                                    aria-hidden="true"
                                />
                                {/* "AI" pill badge */}
                                <span className="absolute -top-2 -right-2 z-10 px-2 py-0.5 rounded-full bg-cyan-300 text-foreground text-[10px] font-bold tracking-wider shadow-md">
                                    {h("cta_primary_badge", "AI · NEW")}
                                </span>
                                <Button
                                    size="lg"
                                    className="relative h-12 px-6 rounded-full bg-gradient-to-r from-primary to-[#003E50] text-white font-semibold tracking-wide hover:from-[#003E50] hover:to-primary transition-all group-hover:scale-[1.03] shadow-lg"
                                    data-testid="hero-simulator-btn"
                                >
                                    <Sparkle size={16} weight="fill" className="mr-2 text-cyan-200" />
                                    {h("cta_primary", "TRY AI SMILE SIMULATOR")}
                                </Button>
                            </Link>
                            <Link to="/smile-assessment" className="relative group">
                                {/* Pulse halo */}
                                <span className="absolute inset-0 rounded-full bg-amber-400/40 blur-md animate-pulse pointer-events-none" aria-hidden="true" />
                                <span className="absolute -top-2 -right-2 z-10 px-2 py-0.5 rounded-full bg-amber-400 text-foreground text-[10px] font-bold tracking-wider shadow-md">
                                    {h("cta_secondary_badge", "FREE")}
                                </span>
                                <Button
                                    size="lg"
                                    variant="outline"
                                    className="relative h-12 px-6 rounded-full border-2 border-amber-400 bg-white hover:bg-amber-50 text-foreground font-semibold transition-all group-hover:scale-[1.03]"
                                    data-testid="hero-assessment-btn"
                                >
                                    <Sparkle size={16} weight="fill" className="mr-2 text-amber-500" />
                                    {h("cta_secondary", "FREE SMILE ASSESSMENT")}
                                </Button>
                            </Link>
                        </div>

                        {/* Tertiary "lower-funnel" link for already-decided visitors */}
                        <div className="mt-4">
                            <Link
                                to="/find-doctor"
                                className="group inline-flex items-center gap-1.5 text-sm font-medium text-foreground/70 hover:text-primary transition-colors"
                                data-testid="hero-already-decided-link"
                            >
                                <span className="text-muted-foreground">{h("tertiary_prefix", "Already decided?")}</span>
                                <span className="underline decoration-primary/40 underline-offset-4 group-hover:decoration-primary">
                                    {h("tertiary_link", "Book a free consultation at a doctor near you")}
                                </span>
                                <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" weight="bold" />
                            </Link>
                        </div>
                        <div className="mt-10 flex items-center gap-6">
                            <div>
                                <div className="font-display text-3xl font-bold">
                                    <AnimatedCounter to={50} suffix="+" testId="kpi-cities" />
                                </div>
                                <div className="text-xs text-muted-foreground">{h("kpi_cities_label", "Cities served")}</div>
                            </div>
                            <div className="w-px h-10 bg-border" />
                            <div>
                                <div className="font-display text-3xl font-bold">
                                    <AnimatedCounter to={500} suffix="+" testId="kpi-clinics" />
                                </div>
                                <div className="text-xs text-muted-foreground">{h("kpi_clinics_label", "Partner clinics")}</div>
                            </div>
                            <div className="w-px h-10 bg-border" />
                            <div>
                                <div className="font-display text-3xl font-bold">
                                    <AnimatedCounter to={4.9} from={4} decimals={1} suffix="★" testId="kpi-rating" />
                                </div>
                                <div className="text-xs text-muted-foreground">{h("kpi_rating_label", "Patient rating")}</div>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.7, delay: 0.2 }}
                        className="lg:col-span-5 relative"
                    >
                        {/* Decorative halos behind the photo */}
                        <div className="absolute -top-8 -right-8 w-72 h-72 rounded-full bg-primary/15 blur-3xl pointer-events-none" aria-hidden="true" />
                        <div className="absolute -bottom-12 -left-10 w-64 h-64 rounded-full bg-amber-200/40 blur-3xl pointer-events-none" aria-hidden="true" />

                        <div className="relative">
                            {/* IDA-certified floating pill (top-right) */}
                            <div className="absolute -top-3 -right-3 z-10 hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white shadow-lg border border-border/70" data-testid="hero-ida-badge">
                                <ShieldCheck size={14} weight="duotone" color="hsl(192 100% 31%)" />
                                <span className="text-[11px] font-semibold tracking-wide">{h("ida_badge_text", "IDA Certified")}</span>
                            </div>

                            {/* Live transformations pill (top-left) */}
                            <div className="absolute -top-3 -left-3 z-10 hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-foreground/90 backdrop-blur text-white shadow-lg" data-testid="hero-live-pill">
                                <span className="relative flex h-2 w-2">
                                    <span className="absolute inset-0 rounded-full bg-green-400 animate-ping opacity-75" />
                                    <span className="relative rounded-full h-2 w-2 bg-green-400" />
                                </span>
                                <span className="text-[11px] font-semibold">{h("live_pill_text", "Live · 12 transformations today")}</span>
                            </div>

                            {/* Photo container */}
                            <div className="relative rounded-3xl overflow-hidden aspect-[4/5] max-h-[600px] shadow-[0_20px_60px_-15px_rgba(0,45,57,0.25)] ring-1 ring-white/40">
                                <img
                                    src="/marketing/landing-showcase.png"
                                    alt="Happy Indian family with bright, healthy smiles"
                                    className="w-full h-full object-cover"
                                />
                                {/* Soft bottom gradient for testimonial readability */}
                                <div className="absolute bottom-0 inset-x-0 h-40 bg-gradient-to-t from-foreground/40 to-transparent pointer-events-none" />

                                {/* Testimonial card */}
                                <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-md rounded-2xl p-4 flex items-center gap-3 shadow-xl">
                                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-primary to-[#003E50] flex items-center justify-center text-white font-display font-bold text-sm shrink-0">
                                        TS
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1 text-amber-500 mb-0.5">
                                            {["a", "b", "c", "d", "e"].map((k) => (
                                                <Star key={k} size={10} weight="fill" />
                                            ))}
                                        </div>
                                        <div className="text-sm font-semibold leading-tight" data-testid="hero-testimonial-quote">&quot;{h("testimonial_quote", "Confident smiles, all four of us.")}&quot;</div>
                                        <div className="text-[11px] text-muted-foreground mt-0.5" data-testid="hero-testimonial-attribution">{h("testimonial_attribution", "The Sharmas, Invisique™ family")}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* TRUST STRIP, certifications */}
            <section className="py-10 bg-gradient-to-r from-secondary/40 via-secondary/20 to-secondary/40 border-y border-border/60">
                <div className="max-w-7xl mx-auto px-5 sm:px-8">
                    <div className="text-center text-xs uppercase tracking-[0.3em] text-muted-foreground/70 mb-6 font-semibold">Trusted & certified by</div>
                    <div className="flex flex-wrap justify-center lg:justify-between gap-3 items-center">
                        {[
                            { label: "Indian Dental Assoc.", icon: ShieldCheck },
                            { label: "Ortho Society", icon: Star },
                            { label: "ISO 13485 Certified", icon: CheckCircle },
                            { label: "Smile India Network", icon: Sparkle },
                            { label: "Medical Council India", icon: ShieldCheck },
                        ].map((l) => (
                            <div
                                key={l.label}
                                className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/60 backdrop-blur border border-border/40 hover:border-primary/30 hover:bg-white transition-all"
                            >
                                <l.icon size={14} weight="duotone" color="hsl(192 100% 31%)" />
                                <span className="text-xs tracking-wider font-semibold text-foreground/70">{l.label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* AS FEATURED IN, press strip removed per user request */}

            {/* HOW IT WORKS */}
            <section id="how" className="py-14 lg:py-20">
                <div className="max-w-7xl mx-auto px-5 sm:px-8">
                    <div className="max-w-2xl mb-14">
                        <div className="overline text-primary mb-4">Simple as 1-2-3</div>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
                            Three steps to <span className="text-primary">a new you.</span>
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6 relative">
                        {/* Connecting line behind the icon ring (desktop only) */}
                        <div
                            className="hidden md:block absolute top-12 left-[16%] right-[16%] h-0.5 bg-gradient-to-r from-primary/30 via-primary/40 to-primary/30 z-0"
                            aria-hidden="true"
                        />
                        {STEPS.map((s, i) => (
                            <motion.div
                                key={s.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.12 }}
                                className="relative p-8 bg-card rounded-2xl bento-shadow z-10 hover:-translate-y-1 transition-transform"
                                data-testid={`step-${i + 1}`}
                            >
                                <div className="flex items-center justify-between mb-6">
                                    <div className="relative w-14 h-14 rounded-xl bg-gradient-to-br from-primary to-[#003E50] flex items-center justify-center ring-4 ring-card shadow-lg">
                                        <s.icon size={26} weight="duotone" color="white" />
                                    </div>
                                    <span className="font-display text-5xl font-bold text-primary/15 leading-none">0{i + 1}</span>
                                </div>
                                <h3 className="font-display text-xl font-semibold mb-3">{s.title}</h3>
                                <p className="text-muted-foreground text-sm leading-relaxed">{s.desc}</p>
                            </motion.div>
                        ))}
                    </div>

                    {/* Three steps CTA, Talk to a Counsellor lead-capture card */}
                    <CounsellorCallbackCard />
                </div>
            </section>

            {/* AI SMILE PREVIEW BANNER removed per request */}

            {/* BENEFITS BENTO */}
            <section className="py-14 lg:py-20 bg-secondary/40 border-y border-border/60">
                <div className="max-w-7xl mx-auto px-5 sm:px-8">
                    <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
                        <div className="lg:col-span-7">
                            <div className="overline text-primary mb-4">Why Invisique™</div>
                            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
                                Built for real life.<br /><span className="text-primary">Backed by real doctors.</span>
                            </h2>
                        </div>
                        <p className="lg:col-span-5 text-muted-foreground">
                            Powered by precision biomechanics and German-engineered digital workflows, every aligner is
                            custom-crafted from your 3D scan and clinically optimised for your anatomy. No wires, no
                            brackets, just confident, trackable progress, visit after visit.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {BENEFITS.map((b, i) => (
                            <motion.div
                                key={b.title}
                                initial={{ opacity: 0, y: 15 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.08 }}
                                className="p-6 bg-white rounded-2xl bento-shadow"
                                data-testid={`benefit-${i}`}
                            >
                                <b.icon size={28} weight="duotone" color="hsl(192 100% 31%)" />
                                <h3 className="font-display text-lg font-semibold mt-4 mb-1">{b.title}</h3>
                                <p className="text-sm text-muted-foreground">{b.desc}</p>
                            </motion.div>
                        ))}
                    </div>

                    {/* Why Invisique CTA
                        2026-02-28 redesign — the prior 2-col `flex justify-between`
                        layout left a big empty middle void on wide screens. New
                        3-zone split fills that gap with social-proof content
                        (avatar stack + ratings + city count) — the highest-leverage
                        thing a top-of-funnel visitor needs to see before clicking. */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="mt-14 flex flex-col lg:flex-row items-stretch gap-6 lg:gap-8 p-7 lg:p-10 rounded-2xl bg-gradient-to-br from-primary/5 via-white to-amber-50/40 border border-primary/15 bento-shadow"
                        data-testid="why-invisique-cta"
                    >
                        {/* LEFT — heading + sub */}
                        <div className="text-center lg:text-left lg:max-w-[280px] xl:max-w-xs flex flex-col justify-center">
                            <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
                                Ready for a smile you'll <span className="text-primary">love?</span>
                            </h3>
                            <p className="text-muted-foreground mt-2 text-sm sm:text-base">
                                Find a certified doctor near you and get started today.
                            </p>
                        </div>

                        {/* MIDDLE — social proof. Vertical dividers on lg+ visually
                            anchor this column as its own block; on mobile it stacks
                            without dividers. */}
                        <div className="flex-1 flex flex-col items-center justify-center gap-3 lg:px-6 lg:border-l lg:border-r lg:border-border/60 py-2">
                            {/* Avatar stack — initials only (no fabricated faces). Tints
                                rotate through the brand palette so the cluster reads
                                as a real team, not a generic stock illustration. */}
                            <div className="flex -space-x-2.5">
                                {[
                                    { initials: "RK", bg: "bg-primary text-white" },
                                    { initials: "PS", bg: "bg-amber-400 text-foreground" },
                                    { initials: "AN", bg: "bg-rose-400 text-white" },
                                    { initials: "VK", bg: "bg-emerald-500 text-white" },
                                    { initials: "TS", bg: "bg-violet-500 text-white" },
                                ].map((a, i) => (
                                    <div
                                        key={i}
                                        className={`w-9 h-9 rounded-full ${a.bg} ring-2 ring-white grid place-items-center text-[11px] font-bold tracking-tight`}
                                        data-testid={`cta-avatar-${i}`}
                                    >
                                        {a.initials}
                                    </div>
                                ))}
                                <div className="w-9 h-9 rounded-full bg-white ring-2 ring-white border border-primary/20 grid place-items-center text-[10px] font-bold text-primary">
                                    +45
                                </div>
                            </div>
                            <div className="text-center">
                                <div className="font-display text-sm sm:text-base font-bold text-foreground">
                                    50+ certified doctors · 12 cities
                                </div>
                                <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground mt-1">
                                    <span className="flex items-center text-amber-500">
                                        {[...Array(5)].map((_, i) => (
                                            <Star key={i} size={11} weight="fill" />
                                        ))}
                                    </span>
                                    <span className="font-semibold text-foreground">4.9</span>
                                    <span>· IDA approved</span>
                                </div>
                            </div>
                        </div>

                        {/* RIGHT — CTA */}
                        <div className="flex items-center justify-center lg:justify-end shrink-0">
                            <Link to="/find-doctor" className="relative group" data-testid="why-invisique-cta-link">
                                <span className="absolute inset-0 rounded-full bg-amber-400/40 blur-md animate-pulse pointer-events-none" aria-hidden="true" />
                                <Button
                                    size="lg"
                                    variant="outline"
                                    className="relative rounded-full h-12 px-7 border-2 border-amber-400 bg-white hover:bg-amber-50 text-foreground font-semibold tracking-wide transition-all group-hover:scale-[1.03]"
                                    data-testid="why-invisique-cta-btn"
                                >
                                    <MapPin size={15} weight="fill" className="mr-2 text-amber-500" />
                                    FIND A DOCTOR
                                </Button>
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* BEFORE / AFTER GALLERY */}
            <section id="transformations" className="py-14 lg:py-20 border-t border-border/60">
                <div className="max-w-7xl mx-auto px-5 sm:px-8">
                    <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
                        <div className="lg:col-span-7">
                            <div className="overline text-primary mb-4">Before · After</div>
                            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
                                See the transformation.<br /><span className="text-primary">Drag to reveal.</span>
                            </h2>
                        </div>
                        <p className="lg:col-span-5 text-muted-foreground">
                            Real patients, real results. Slide the handle on each photo to see the difference
                            clear aligners make.
                        </p>
                    </div>

                    <div className="grid lg:grid-cols-12 gap-8 items-start">
                        {/* Slider — capped at ~640px so the image doesn't dominate the section */}
                        <div className="lg:col-span-7">
                            <div className="max-w-xl mx-auto lg:mx-0">
                                <BeforeAfterSlider
                                    key={activeCase.id}
                                    before={activeCase.before}
                                    after={activeCase.after}
                                    composite={activeCase.composite}
                                />
                                <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm">
                                    <div>
                                        <div className="font-display text-lg font-semibold">{activeCase.name}</div>
                                        <div className="text-muted-foreground text-xs">{activeCase.case} · {activeCase.city}</div>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-1.5" data-testid="transformation-active-chips">
                                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${COMPLEXITY_TONE[activeCase.complexity]}`}>
                                            {activeCase.complexity}
                                        </span>
                                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-primary/10 text-primary border border-primary/20">
                                            {activeCase.aligners} aligners
                                        </span>
                                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-foreground/5 text-foreground/80 border border-border">
                                            {activeCase.months} months
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Case selector */}
                        <div className="lg:col-span-5 space-y-3" data-testid="transformations-selector">
                            {TRANSFORMATIONS.map((t) => {
                                const active = t.id === activeCase.id;
                                return (
                                    <button
                                        key={t.id}
                                        onClick={() => setActiveCase(t)}
                                        className={`w-full text-left p-4 rounded-2xl border flex items-center gap-4 transition-all ${
                                            active
                                                ? "border-primary bg-primary/5 shadow-sm"
                                                : "border-border bg-card hover:border-primary/50"
                                        }`}
                                        data-testid={`transformation-${t.id}`}
                                    >
                                        <div className="w-14 h-14 rounded-xl overflow-hidden shrink-0 bg-foreground/5 relative">
                                            {t.composite ? (
                                                <img
                                                    src={t.composite}
                                                    alt={t.name}
                                                    className="absolute max-w-none"
                                                    style={{ width: "200%", height: "auto", right: 0, left: "auto", top: "50%", transform: "translateY(-50%)" }}
                                                />
                                            ) : (
                                                <img src={t.after} alt={t.name} className="w-full h-full object-cover" />
                                            )}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="font-display font-semibold truncate">{t.name}</div>
                                            <div className="text-xs text-muted-foreground truncate">{t.case}</div>
                                            <div className="mt-1.5 flex items-center gap-1 flex-wrap">
                                                <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${COMPLEXITY_TONE[t.complexity]}`}>
                                                    {t.complexity}
                                                </span>
                                                <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                                                    {t.aligners} aligners
                                                </span>
                                                <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-foreground/5 text-foreground/70 border border-border">
                                                    {t.months} mo
                                                </span>
                                            </div>
                                        </div>
                                        {active && <CheckCircle size={18} weight="fill" color="hsl(192 100% 31%)" />}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Before/After CTA, light card to contrast with the dark Steps banner */}
                    <div className="mt-12 relative overflow-hidden rounded-3xl bg-white border border-primary/15 p-7 sm:p-9 lg:p-11 bento-shadow" data-testid="ba-cta-banner">
                        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-amber-200/40 blur-3xl pointer-events-none" aria-hidden="true" />
                        <div className="absolute -bottom-24 -left-16 w-56 h-56 rounded-full bg-primary/10 blur-3xl pointer-events-none" aria-hidden="true" />

                        <div className="relative grid lg:grid-cols-12 gap-6 items-center">
                            <div className="lg:col-span-7">
                                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-200/80 text-amber-700 text-[11px] font-bold tracking-[0.2em] uppercase mb-4">
                                    <Star size={11} weight="fill" /> Real patients · Real results
                                </div>
                                <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-[1.15]">
                                    Could this be{" "}
                                    <span className="text-primary">your smile next?</span>
                                </h3>
                                <p className="text-muted-foreground mt-3 max-w-xl text-sm sm:text-base leading-relaxed">
                                    Preview your post-Invisique™ smile with our AI in 10 seconds, or get a personalised cost estimate from a certified doctor near you.
                                </p>

                                <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-xs text-foreground/75">
                                    {["10-second AI preview", "Free first consultation", "500+ certified clinics"].map((v) => (
                                        <div key={v} className="flex items-center gap-1.5">
                                            <CheckCircle size={14} weight="fill" className="text-primary" />
                                            <span>{v}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="lg:col-span-5 flex flex-col gap-3 lg:items-stretch">
                                <Link to="/smile-simulator" className="relative group" data-testid="ba-cta-simulator-link">
                                    <span
                                        className="absolute -inset-1 rounded-full bg-gradient-to-r from-primary via-cyan-400 to-primary bg-[length:200%_100%] opacity-70 blur-md group-hover:opacity-100 transition-opacity"
                                        style={{ animation: "shimmer 3s linear infinite" }}
                                        aria-hidden="true"
                                    />
                                    <Button
                                        size="lg"
                                        className="relative w-full h-12 rounded-full bg-gradient-to-r from-primary to-[#003E50] text-white font-semibold tracking-wide hover:from-[#003E50] hover:to-primary transition-all group-hover:scale-[1.02] shadow-lg"
                                        data-testid="ba-cta-simulator-btn"
                                    >
                                        <Sparkle size={15} weight="fill" className="mr-2 text-cyan-200" />
                                        TRY AI SMILE SIMULATOR
                                    </Button>
                                </Link>
                                <Link to="/find-doctor" className="relative group" data-testid="ba-cta-find-link">
                                    <span className="absolute inset-0 rounded-full bg-amber-400/40 blur-md animate-pulse pointer-events-none" aria-hidden="true" />
                                    <Button
                                        size="lg"
                                        variant="outline"
                                        className="relative w-full h-12 rounded-full border-2 border-amber-400 bg-white hover:bg-amber-50 text-foreground font-semibold tracking-wide transition-all group-hover:scale-[1.02]"
                                        data-testid="transformations-cta-btn"
                                    >
                                        <MapPin size={15} weight="fill" className="mr-2 text-amber-500" />
                                        FIND A DOCTOR
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* RESULTS — "Real stories" */}
            <section className="py-14 lg:py-20">
                <div className="max-w-7xl mx-auto px-5 sm:px-8">
                    <div className="mb-14">
                        <div className="overline text-primary mb-4">Real stories</div>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
                            Results that <span className="text-primary">speak for themselves.</span>
                        </h2>
                    </div>
                    {/* 2026-02-25: Decoupled from the TRANSFORMATIONS data above (which
                        drives the before/after slider with side-by-side composites).
                        REAL_STORIES uses full portraits with normal object-cover crops
                        — 4 cards in a responsive grid. */}
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
                        {realStories.map((s) => (
                            <div key={s.id} className="rounded-2xl overflow-hidden bento-shadow group bg-card border border-border/60 hover:border-primary/30 transition-all hover:-translate-y-0.5" data-testid={`story-${s.id}`}>
                                <div className="aspect-[4/5] overflow-hidden relative bg-foreground/5">
                                    <img
                                        src={s.image}
                                        alt={`${s.name} — Invisique™ patient`}
                                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                                        loading="lazy"
                                    />
                                    {/* Bottom-gradient for legibility of the floating chip */}
                                    <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/65 to-transparent" aria-hidden="true" />
                                    <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between gap-2">
                                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur-sm text-foreground text-[10px] font-bold tracking-wider uppercase shadow-sm">
                                            <CheckCircle size={11} weight="fill" className="text-emerald-600" />
                                            Verified
                                        </div>
                                        <div className="inline-flex items-center gap-1 text-amber-300">
                                            {["s1", "s2", "s3", "s4", "s5"].map((k) => <Star key={k} size={11} weight="fill" />)}
                                        </div>
                                    </div>
                                </div>
                                <div className="p-5 lg:p-6">
                                    <p className="text-sm text-foreground leading-relaxed">"{s.quote}"</p>
                                    <div className="mt-4 pt-4 border-t border-border/70">
                                        <div className="font-semibold text-sm">{s.name}</div>
                                        <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5">
                                            <MapPin size={11} weight="fill" className="text-primary/60" />
                                            <span>{s.city} · {s.case}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-14 lg:py-16 bg-secondary/40 border-y border-border/60">
                <div className="max-w-4xl mx-auto px-5 sm:px-8">
                    <div className="mb-10 text-center">
                        <div className="overline text-primary mb-4">Common questions</div>
                        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
                            Frequently asked. <span className="text-primary">Honestly answered.</span>
                        </h2>
                    </div>
                    <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
                        {FAQ.map((f) => (
                            <AccordionItem key={f.q} value={f.q} className="bg-white rounded-xl mb-3 px-5 border border-border/60">
                                <AccordionTrigger className="text-left font-display text-base font-semibold">{f.q}</AccordionTrigger>
                                <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>
        </div>
    );
}
